import { useCallback } from 'react';
import { ParsedQuery } from 'query-string';
import { useLocation } from 'react-router-dom';
import { useFilters } from './useFilters';
import { filterEmptyFilters } from '../helpers/helpers';
import {
  deleteParticipant,
  getParticipantsRequest,
  ISurveyFilters,
  IPostParticipantBody,
  IPostTeamMemberBody,
  patchParticipant,
  patchParticipantTeamMember,
  postAddMemberToParticipantsTeam,
  postParticipant,
} from '../services/surveys';

export const useParticipantsTeam = () => {
  const { search } = useLocation();
  const { currentFilters, applyFilters } = useFilters();

  const getAppliedFilters = useCallback(() => {
    const params = Object.fromEntries(new URLSearchParams(search));
    delete params.card;
    return Object.fromEntries(new URLSearchParams(params));
  }, [search]);

  const normalizeFilters = (filters: ParsedQuery): ISurveyFilters => {
    return Object.entries(filters).reduce<ISurveyFilters>((acc, [key, value]) => {
      if (Array.isArray(value)) {
        acc[key] = value.find((entry): entry is string => typeof entry === 'string') ?? null;
      } else if (typeof value === 'string' || value === null || value === undefined) {
        acc[key] = value;
      } else {
        acc[key] = String(value);
      }
      return acc;
    }, {});
  };

  const downloadParticipants = async (surveyId: string, overrideFilters: ParsedQuery = {}) => {
    const { members, ...filters } = currentFilters;
    const normalizedCurrentFilters = normalizeFilters(filters as ParsedQuery);
    const normalizedOverrideFilters = normalizeFilters(overrideFilters);
    if (!normalizedCurrentFilters.perPage) {
      return await getParticipantsRequest(surveyId, {
        ...normalizedCurrentFilters,
        ...normalizedOverrideFilters,
        perPage: '7',
      });
    }
    return await getParticipantsRequest(surveyId, {
      ...normalizedCurrentFilters,
      ...normalizedOverrideFilters,
    });
  };

  const applyManyPartiMembersTileFilter = (
    memberFilter: Record<string, string | null | undefined>,
    ids: string[],
    additionalFilters?: Record<string, string | null | undefined>
  ) => {
    const { members } = currentFilters;
    const membersObj = members ? JSON.parse(members as string) : {};
    const objReadyForStringify = ids.reduce(
      (resp: Record<string, unknown>, id: string) => {
        if (id) {
          const currentMembers = membersObj[id] ? membersObj[id] : {};
          const newMembers = { ...currentMembers, ...memberFilter };
          const filteredMembers = filterEmptyFilters(newMembers);
          return { ...resp, [id]: filteredMembers };
        }
        return resp;
      },
      { ...membersObj }
    );
    const stringifiedObject = JSON.stringify(objReadyForStringify);
    return applyFilters({ members: stringifiedObject, ...additionalFilters });
  };

  const applyPartiMembersTileFilter = (filterObj: Record<string, string | null | undefined>, id: string) => {
    const { members } = currentFilters;
    const membersObj = members ? JSON.parse(members as string) : {};
    const currentMembers = membersObj[id] ? membersObj[id] : {};
    const newMembers = { ...currentMembers, ...filterObj };
    const filteredMembers = filterEmptyFilters(newMembers);
    const objReadyForStringify = { ...membersObj, [id]: filteredMembers };
    const stringifiedObject = JSON.stringify(objReadyForStringify);
    return applyFilters({ members: stringifiedObject });
  };

  const addParticipantsTeamMember = async (
    body: IPostTeamMemberBody,
    surveyId: string,
    teamId: string
  ) => {
    return await postAddMemberToParticipantsTeam(body, surveyId, teamId);
  };

  const deleteParticipantsTeamMember = async (surveyId: string, participantId: string) => {
    return await deleteParticipant(surveyId, participantId);
  };

  const editParticipantsTeamMember = async (
    body: IPostTeamMemberBody,
    surveyId: string,
    teamId: string,
    memberId: string
  ) => {
    return await patchParticipantTeamMember(body, surveyId, teamId, memberId);
  };

  const submitParticipant = async (surveyId: string, data: IPostParticipantBody) => {
    return await postParticipant(surveyId, data);
  };

  const patchParticipantMember = async (
    surveyId: string,
    body: IPostParticipantBody,
    participantId: string
  ) => {
    return await patchParticipant(surveyId, body, participantId);
  };

  return {
    applyPartiMembersTileFilter,
    applyManyPartiMembersTileFilter,
    addParticipantsTeamMember,
    editParticipantsTeamMember,
    deleteParticipantsTeamMember,
    getAppliedFilters,
    downloadParticipants,
    submitParticipant,
    patchParticipantMember,
  };
};
